import {DataStatus, Form, FormField, FormSubmitHandler} from 'platform/components';
import {Box} from 'platform/foundation';

import {useGetAvailableLanguagesListQuery} from '@dms/api/core';
import {useSetUserSettingsItemMutation} from '@dms/api/customer';
import {browserStorageKey} from '@dms/config';
import i18n from '@dms/i18n';
import {getWorkspaceFromUri, testIds} from '@dms/routes';
import {handleApiError} from '@dms/shared';

import {ApiError} from 'shared/web';

import {MyProfileFooter} from '../../../components/MyProfileFooter';
import {MyProfileModuleProps} from '../../../types/MyProfileModuleProps';
import {LanguageFormSchema, LanguageFormSchemaType} from './LanguageFormSchema';

const {workspace} = getWorkspaceFromUri();

export function LanguageForm(props: MyProfileModuleProps) {
  const [updateUserSettings] = useSetUserSettingsItemMutation();
  const {data, isLoading, isError} = useGetAvailableLanguagesListQuery({
    workspace: workspace as string,
  });

  const handleSubmit: FormSubmitHandler<LanguageFormSchemaType> = async (
    values,
    _setErrors,
    reset,
    blockerContext
  ) => {
    await updateUserSettings({
      settingKey: 'selected_language',
      userSettingRequestBody: {
        value: values.language,
      },
    })
      .unwrap()
      .then(props.purge)
      .then(() => i18n.changeLanguage(values.language))
      .then(() => {
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        reset(undefined, {keepValues: true});
        localStorage.setItem(browserStorageKey.LAST_KNOWN_LANGUAGE, values.language);
        window.location.reload();
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  const languageOptions = Object.entries(data?.languages ?? {}).map(([value, label]) => ({
    label,
    value,
  }));

  const defaultValues = {
    language:
      i18n?.resolvedLanguage ||
      (localStorage[browserStorageKey.LAST_KNOWN_LANGUAGE] as string | undefined) ||
      data?.default,
  };

  return (
    <Box maxWidth={100}>
      <DataStatus isLoading={isLoading} isError={isError}>
        <Form<LanguageFormSchemaType>
          shouldWatchForUnsavedChanges
          onSubmit={handleSubmit}
          defaultValues={defaultValues}
          experimentalZodSchema={LanguageFormSchema}
        >
          {(control, formApi) => (
            <>
              <FormField
                control={control}
                isRequired
                type="choice"
                isNotClearable
                name="language"
                options={languageOptions}
                data-testid={testIds.myProfile.language('language')}
              />

              <MyProfileFooter
                actions={[
                  {
                    type: 'button',
                    onClick: () => formApi.reset(defaultValues),
                    title: i18n.t('general.actions.discardChanges'),
                    variant: 'secondary',
                    'data-testid': testIds.myProfile.language('discard'),
                  },
                  {
                    type: 'form-button',
                    control,
                    buttonType: 'submit',
                    title: i18n.t('general.actions.saveChanges'),
                    'data-testid': testIds.myProfile.language('save'),
                  },
                ]}
              />
            </>
          )}
        </Form>
      </DataStatus>
    </Box>
  );
}
